import dayjs from "dayjs"
import BaseModal from "../../../../../components/layout/BaseModal"
import { Key, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import ErrorPage from "../../../../404/ErrorPage"
import Loading from "../../../../../components/common/Loading"
import { transactionElementType } from "../../../types"
import { AssignBillElement } from "./AssignBillElement"
import { TransactionRow } from "./TransactionRow"
import BaseSearchInputField from "../../../../../components/elements/BaseSearchInputField"
import { getPaginatedData } from "../../../../api/apiCalls"
import { Pagination } from "antd"
import { formatCurrency, returnThousanderBreakInNumber, useDelayWhenSearchWithPagination } from "../../../../../services/functions"
import BaseCheckbox from "../../../../../components/elements/BaseCheckbox"
import { defaultTransactionStatus } from "../../../../../data/defaultValues"
import { getAssignedTransactionsOfPayment } from "../../../api/apiCalls"
import { ReactComponent as RELOAD } from "../../../../../assets/RefreshICON.svg"
import { useCreateCreditMutation, useIgnoreTransactionMutation, useRefetchTransactions, useUpdateTransactionMutation, useUpdateTransactionPullMutation } from "../../../api/services"
import { ReactComponent as CLOSE } from "../../../../../assets/CloseICON.svg"
import { PositionData, PR } from "../../../../../data/Types"
import BasePopover from "../../../../../components/elements/BasePopover"
import { GeneralQueryKeys } from "../../../../../api/constants"
import BaseGrowBody from "../../../../../components/elements/BaseGrowBody"
import BaseInputField from "../../../../../components/elements/BaseInputField"
import BaseInputArea from "../../../../../components/elements/BaseInputArea"
import { Authorized } from "../../../../../components/authentication/Authorized"
import { P } from "../../../../../auth/permissions"

type RecheckPaymentModalType = {
    modalId: number
    payment?: PR
    refreshDataPayment: string
    makePayreqUnpaid: () => void
}

export default function RecheckPaymentModal(props: RecheckPaymentModalType) {
    const [page, setpage] = useState(1)
    const handlePageChange = (page: number) => {
        setpage(page)
    }
    const [searchTerm, setsearchTerm] = useState("")
    const [searchStatus, setsearchStatus] = useState<(typeof defaultTransactionStatus)[keyof typeof defaultTransactionStatus]>("UNASSIGNED")
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const queryKey = GeneralQueryKeys.TRANSACTIONS_ALL + page + searchParams + searchStatus
    const { data, isLoading, error } = useQuery({
        queryKey: [queryKey],
        queryFn: () => getPaginatedData({ pageType: "banking", page: page, search: searchParams, limit: 15, status: searchStatus }),
    })

    const queryClient = useQueryClient()
    const { mutate: refreshTransactions } = useRefetchTransactions(queryClient, queryKey)

    const queryKeyAssigned = GeneralQueryKeys.TRANSACTIONS_ASSIGNED + props.payment?._id + props.payment?.transactions
    const {
        data: assignedData,
        isLoading: assignedLoading,
        error: assignedError,
    } = useQuery({
        queryKey: [queryKeyAssigned],
        queryFn: () => getAssignedTransactionsOfPayment(props.payment?.transactions ?? []),
        enabled: !!props.payment?.transactions,
    })

    const [furtherPayment, setfurtherPayment] = useState(false)
    const { mutate: assignTransaction } = useUpdateTransactionMutation(queryClient, props.refreshDataPayment, queryKey)
    const handleAssignTransactionToPayreq = (tid: string) => {
        assignTransaction({
            pid: props.payment?._id ?? "",
            tid: tid,
        })
    }
    const { mutate: unassignTransaction } = useUpdateTransactionPullMutation(queryClient, props.refreshDataPayment, queryKey)
    const handleUnassignTransactionFromPayreq = (tid: string) => {
        unassignTransaction({
            pid: props.payment?._id ?? "",
            tid: tid,
        })
    }
    const { mutate: ignoreTransaction } = useIgnoreTransactionMutation(queryClient, queryKey)
    const handleIgnoreTransaction = (tid: string) => {
        ignoreTransaction(tid)
    }

    const displayedTransactions = useMemo(() => data?.docs, [data?.docs])

    const [newCredit, setNewCredit] = useState(false)
    const [newCreditAmount, setNewCreditAmount] = useState(0)
    const [newCreditComment, setNewCreditComment] = useState("")
    const [positionRefunds, setPositionRefunds] = useState<{ [key: string]: { amountNetto: number; amountBrutto: number } }>({})
    const { mutate: createCredit, isPending: creatingCredit } = useCreateCreditMutation(queryClient, props.refreshDataPayment)
    const handleCreateCredit = () => {
        // Transform positionRefunds object into array, filtering out positions with 0 amounts
        const positions =
            props.payment?.bill_id?.positions
                ?.filter((position: PositionData) => {
                    const refund = positionRefunds[position.position_id]
                    return refund && (refund.amountNetto > 0 || refund.amountBrutto > 0)
                })
                .map((position: PositionData) => {
                    const amounts = positionRefunds[position.position_id]
                    const reducedAmount = amounts.amountNetto / position.singlePrice

                    return {
                        position_id: position.position_id,
                        refundAmountNetto: amounts.amountNetto,
                        refundAmountBrutto: amounts.amountBrutto,
                        reducedAmount: Math.round(reducedAmount * 100) / 100, // Round to 2 decimals
                        description: position.description,
                        unit: position.unit,
                        tax: position.tax,
                    }
                }) ?? []

        const payload = {
            bill: props.payment?.bill_id?._id ?? "",
            paymentRequirement: props.payment?._id ?? "",
            comment: newCreditComment,
            positions,
        }

        // Formular erst nach Erfolg schließen; bei Fehler (z. B. 403 ohne rbac_buchhaltung_read_write) bleibt es offen
        createCredit(payload, { onSuccess: () => setNewCredit(false) })
    }

    // Close Modal and Clear or Close Everything
    const closeModalAndClearOrCloseEverything = () => {
        setfurtherPayment(false)
        setNewCredit(false)
        setNewCreditAmount(0)
        setNewCreditComment("")
        setPositionRefunds({})
        setsearchTerm("")
        setsearchStatus(defaultTransactionStatus.UNASSIGNED)
        setpage(1)
    }

    console.log(props)

    return (
        <BaseModal title="Zahlungen verwalten" modalId={props.modalId} onClose={closeModalAndClearOrCloseEverything}>
            {error && <ErrorPage />}
            {(isLoading || assignedLoading) && <Loading />}
            {props.payment && data && !error && !isLoading && (
                <div className="flex flex-col h-full overflow-hidden">
                    {/* Basic Information */}
                    <div className="flex flex-col md:flex-row max-h-[8rem] md:max-h-none overflow-y-scroll md:overflow-hidden gap-2 md:gap-8 p-4 border-b w-full">
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">Zahlung für Nr.</p>
                            <p className="font-medium">{props.payment?.bill_id?.nr}</p>
                        </div>
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">Typ</p>
                            <p className="font-medium">{props.payment.bill_id?.billType}</p>
                        </div>
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">Betrag</p>
                            <p className="font-medium">{returnThousanderBreakInNumber(Number(props.payment.expected_payment))} €</p>
                        </div>
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">ausgestellt am</p>
                            <p className={`font-medium`}>{dayjs(props.payment.invoice_date).format("DD.MM.YYYY")}</p>
                        </div>
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">fällig am</p>
                            <p className={`font-medium`}>{dayjs(props.payment.due_date).format("DD.MM.YYYY")}</p>
                        </div>
                        <div className="flex flex-col justify-between">
                            <p className="text-sm text-gray-500">Status</p>
                            <p className={`font-medium ${props.payment.status === "settled" ? "text-green-600" : "text-red-600"}`}>{props.payment.status === "settled" ? "Beglichen" : "Offen"}</p>
                        </div>
                        {props.payment.credit && (
                            <div className="flex flex-col justify-between">
                                <p className="text-sm text-gray-500">Gutschrift</p>
                                <p className={`font-medium`}>{formatCurrency(props.payment.credit.amount)}</p>
                            </div>
                        )}
                        {props.payment.manuallySettle && (assignedData?.length === 0 || !assignedData) && (
                            <div className="flex flex-col justify-center cursor-pointer">
                                <BasePopover label={'Auf "Unbeglichen" setzen?'} text="Wurde die Zahlung fälschlich als beglichen markiert?" okButtonClick={props.makePayreqUnpaid}>
                                    <CLOSE className="w-6 h-6" />
                                </BasePopover>
                            </div>
                        )}
                        {assignedData && assignedData.length > 0 && (
                            <div className="flex flex-col justify-center text-gray-400 text-xs md:pl-4">
                                <p className="text-xs">Offener Betrag</p>
                                <p className={`font-medium text-xs`}>
                                    {returnThousanderBreakInNumber(
                                        Number(props.payment.expected_payment) -
                                            Number(assignedData.reduce((acc: number, item: transactionElementType) => acc + Number(item.paymentInformation.transactionAmount.amount), 0))
                                    )}{" "}
                                    €
                                </p>
                            </div>
                        )}
                    </div>
                    {/* Show PayReq if zugeordnet */}
                    <div className="flex flex-col p-4 border-b w-full">
                        {assignedData?.length === 0 && <p className="text-center font-medium">Keine Zahlung zugeordnet</p>}
                        {assignedError && <ErrorPage />}
                        {assignedData &&
                            assignedData?.map((item: transactionElementType, index: number) => (
                                <TransactionRow transaction={item} key={index} remove={() => handleUnassignTransactionFromPayreq(item._id)} />
                            ))}
                    </div>
                    {!furtherPayment && !newCredit && (
                        <>
                            {props.payment.status === "unpaid" && !props.payment.credit && (
                                // POST /credit verlangt rbac_buchhaltung_read_write (HM-004)
                                <Authorized allOf={[P.buchhaltung_read_write]}>
                                    <button className="btn btn-primary btn-sm mt-2" disabled={creatingCredit} onClick={() => setNewCredit(true)}>
                                        Gutschrift erstellen
                                    </button>
                                </Authorized>
                            )}
                            <button className="btn btn-primary btn-sm mt-2" onClick={() => setfurtherPayment(!furtherPayment)}>
                                Weitere Transaktion zuweisen
                            </button>
                        </>
                    )}
                    {/* Transaction List */}
                    {furtherPayment && (
                        <>
                            {/* UPPER BUTTON ROW */}
                            <div className="flex flex-col w-full pt-2 py-2">
                                {/* Search and Refresh Button */}
                                <div className="flex justify-between w-full">
                                    <BaseSearchInputField small placeholder="Nach Verwendungszweck/Preis suchen" searchFunction={setsearchTerm} value={searchTerm} />
                                    <div className="flex items-center">
                                        <button className="btn btn-sm btn-primary mr-2" onClick={() => refreshTransactions()}>
                                            <RELOAD className="h-6 w-6" />
                                        </button>
                                        <button className="btn btn-sm btn-primary mr-2" onClick={() => setfurtherPayment(!furtherPayment)}>
                                            Abbrechen
                                        </button>
                                    </div>
                                </div>
                                {/* Filter Checkboxes */}
                                <div className="hidden md:flex justify-between py-2 gap-2">
                                    <BaseCheckbox
                                        check={searchStatus === defaultTransactionStatus.UNASSIGNED}
                                        setcheck={() => setsearchStatus(defaultTransactionStatus.UNASSIGNED)}
                                        label={"Nicht Zugeordnete Transaktionen anzeigen"}
                                    />
                                    <BaseCheckbox
                                        check={searchStatus === defaultTransactionStatus.ASSIGNED}
                                        setcheck={() => setsearchStatus(defaultTransactionStatus.ASSIGNED)}
                                        label={"Zugeordnete Transaktionen anzeigen"}
                                    />
                                    <BaseCheckbox
                                        check={searchStatus === defaultTransactionStatus.IGNORE}
                                        setcheck={() => setsearchStatus(defaultTransactionStatus.IGNORE)}
                                        label={"Ignorierte Transaktionen anzeigen"}
                                    />
                                </div>
                            </div>
                            {/* TRANSACTIONS */}
                            <BaseGrowBody classProps="overflow-y-scroll">
                                {displayedTransactions?.map(
                                    (
                                        item: {
                                            _id: string
                                            processingStatus: string
                                            bookingTimestamp: string | number | Date | dayjs.Dayjs | null | undefined
                                            paymentInformation: { transactionAmount: { amount: string }; remittanceText: string }
                                        },
                                        index: Key | null | undefined
                                    ) => (
                                        <AssignBillElement
                                            unassignTransaction={() => handleUnassignTransactionFromPayreq(item._id)}
                                            assignTransaction={() => handleAssignTransactionToPayreq(item._id)}
                                            ignoreTransaction={() => handleIgnoreTransaction(item._id)}
                                            key={index}
                                            alreadyAssignedToPayreq={props.payment?.transactions?.includes(item._id) ?? false}
                                            alreadyIgnored={item.processingStatus === defaultTransactionStatus.IGNORE}
                                            date={dayjs(item.bookingTimestamp).format("DD.MM.YYYY")}
                                            information={item}
                                        />
                                    )
                                )}
                            </BaseGrowBody>
                            <div className="flex justify-center w-full p-4">
                                <Pagination defaultCurrent={1} current={page} pageSize={15} onChange={handlePageChange} total={data.totalDocs} showSizeChanger={false} />
                            </div>
                        </>
                    )}
                    {newCredit && (
                        <BaseGrowBody classProps="overflow-y-auto">
                            <div className="flex flex-col p-4 w-full gap-4">
                                {/* Positions Header */}
                                <div className="text-lg font-semibold">Rechnungspositionen</div>

                                {/* Positions List */}
                                <div className="flex flex-col gap-3">
                                    {props.payment?.bill_id?.positions?.map((position: PositionData, index: number) => {
                                        const nettoPrice = position.singlePrice * position.amount
                                        const bruttoPrice = nettoPrice * (1 + position.tax / 100)
                                        const refundedAmount = positionRefunds[position.position_id]?.amountNetto
                                            ? (positionRefunds[position.position_id].amountNetto / position.singlePrice).toFixed(2)
                                            : 0

                                        return (
                                            <div key={position.position_id || index} className="border rounded-lg p-4 bg-gray-50">
                                                {/* Position Header */}
                                                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                                    <span className="font-semibold text-sm">Position {position.position_id || index + 1}</span>
                                                    <span className="text-gray-600">•</span>
                                                    <span className="text-sm">
                                                        {position.amount} {position.unit}
                                                    </span>
                                                    {Number(refundedAmount) > 0 && (
                                                        <span className="text-sm text-red-600 font-medium">
                                                            (-{refundedAmount} {position.unit})
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Position Details Grid */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-gray-500">Beschreibung</span>
                                                        <span className="font-medium">{position.description}</span>
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-gray-500">Steuersatz</span>
                                                        <span className="font-medium">{position.tax}%</span>
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-gray-500">Netto-Preis</span>
                                                        <span className="font-medium">{formatCurrency(nettoPrice)}</span>
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-gray-500">Brutto-Preis</span>
                                                        <span className="font-medium">{formatCurrency(bruttoPrice)}</span>
                                                    </div>
                                                </div>

                                                {/* Refund Inputs */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t">
                                                    <BaseInputField
                                                        label="Erstattungsbetrag (Netto) in €"
                                                        value={positionRefunds[position.position_id]?.amountNetto || 0}
                                                        onChange={(e) => {
                                                            const nettoValue = Number(e)
                                                            const bruttoValue = nettoValue * 1.19 // IMPORTANT: 19% is hardcoded here - customer request
                                                            setPositionRefunds({
                                                                ...positionRefunds,
                                                                [position.position_id]: {
                                                                    amountNetto: nettoValue,
                                                                    amountBrutto: Math.round(bruttoValue * 100) / 100, // Round to 2 decimals
                                                                },
                                                            })
                                                        }}
                                                        type="number"
                                                    />

                                                    <BaseInputField
                                                        label="Erstattungsbetrag (Brutto) in €"
                                                        value={positionRefunds[position.position_id]?.amountBrutto || 0}
                                                        onChange={(e) => {
                                                            const bruttoValue = Number(e)
                                                            const nettoValue = bruttoValue / 1.19 // IMPORTANT: 19% is hardcoded here - customer request
                                                            setPositionRefunds({
                                                                ...positionRefunds,
                                                                [position.position_id]: {
                                                                    amountNetto: Math.round(nettoValue * 100) / 100, // Round to 2 decimals
                                                                    amountBrutto: bruttoValue,
                                                                },
                                                            })
                                                        }}
                                                        type="number"
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Comment Section */}
                                <BaseInputArea label="Kommentar" value={newCreditComment} onChange={(e) => setNewCreditComment(e)} />

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-2 w-full pb-4">
                                    <button className="btn btn-ghost btn-sm mt-2" disabled={creatingCredit} onClick={() => setNewCredit(false)}>
                                        Abbrechen
                                    </button>
                                    <button className="btn btn-primary btn-sm mt-2" disabled={creatingCredit} onClick={handleCreateCredit}>
                                        {creatingCredit && <span className="loading loading-spinner loading-xs" />}
                                        Gutschrift erstellen
                                    </button>
                                </div>
                            </div>
                        </BaseGrowBody>
                    )}
                </div>
            )}
        </BaseModal>
    )
}
